import isPostalCode, { locales, PostalCodeLocale } from 'validator/es/lib/isPostalCode';

/**
 * Per-country postal code rules.
 * The formats themselves come from `validator`'s `isPostalCode`, so no postal
 * code regexes are maintained in this repo. What this module adds on top is the
 * data the error message needs: one canonical sample postal code per country.
 */

export const POSTAL_CODE_EXAMPLES: { [isoCode: string]: string } = {
  AT: '1010',
  BE: '1000',
  BG: '1000',
  CA: 'K1A 0B1',
  CH: '8001',
  CY: '1010',
  CZ: '110 00',
  DE: '10115',
  DK: '1050',
  EE: '10111',
  ES: '28001',
  FI: '00100',
  FR: '75001',
  GB: 'SW1A 1AA',
  GR: '104 31',
  HR: '10000',
  HU: '1051',
  IE: 'D02 AF30',
  IS: '101',
  IT: '00100',
  LI: '9490',
  LT: 'LT-01100',
  LU: '1009',
  LV: 'LV-1010',
  MT: 'VLT 1117',
  NL: '1012 AB',
  NO: '0010',
  PL: '00-001',
  PT: '1000-001',
  RO: '010011',
  SE: '111 20',
  SI: '1000',
  SK: '811 01',
  US: '10001'
};

/**
 * Formats `validator` has no locale for. Cyprus is the only EU country missing
 * from its list
 */
const EXTRA_PATTERNS: { [isoCode: string]: RegExp } = {
  CY: /^\d{4}$/
};

export interface IPostalCodeFormat {
  /** Normalised ISO 3166-1 alpha-2 code. */
  isoCode: string;
  /** Sample postal code shown to the user when the input does not match. */
  example: string;
  /** Length, when the format is a plain fixed-length run of digits. */
  digits?: number;
}

const normalizeIsoCode = (isoCode?: string | null) => (isoCode ? isoCode.trim().toUpperCase() : '');

/**
 * Returns the postal code rule for a country, or `undefined` when we have no
 * sample for it or nothing can validate it.
 */
export function getPostalCodeFormat(isoCode?: string | null): IPostalCodeFormat | undefined {
  const code = normalizeIsoCode(isoCode);
  const example = POSTAL_CODE_EXAMPLES[code];

  if (!example) {
    return undefined;
  }

  if (!EXTRA_PATTERNS[code] && !locales.includes(code as PostalCodeLocale)) {
    return undefined;
  }

  return {
    isoCode: code,
    example,
    digits: /^\d+$/.test(example) ? example.length : undefined
  };
}

export function isValidPostalCode(zipCode: string, format: IPostalCodeFormat): boolean {
  const value = zipCode.trim();
  const extraPattern = EXTRA_PATTERNS[format.isoCode];

  if (extraPattern) {
    return extraPattern.test(value);
  }

  return isPostalCode(value, format.isoCode as PostalCodeLocale);
}
